import { IMAGE_CDN_BASE_URL } from '@core/app-config';
import toAbsoluteUrl, {
  isRelativePath,
  isDataUri,
  isBlobUri,
} from '@core/external-links/toAbsoluteUrl';
import type { Options } from '@core/images-ui/types';

type Src = { src: string };

// Fallback for environments where IMAGE_CDN_BASE_URL is not injected (e.g. CF Workers).
// An empty fallback would produce broken relative URLs; use the prod PixelForge base instead.
const FALLBACK_IMAGE_CDN_BASE_URL =
  'https://images.onepeloton.com/peloton-cycle/image/fetch';

export const toImageCdnSrcPrefix = () =>
  IMAGE_CDN_BASE_URL ?? FALLBACK_IMAGE_CDN_BASE_URL;

// CDN does not accept fractional numbers for width or height transformations
const toImageCdnSrcSafeDimension = (num: any) => {
  const coercedNumber = Number(num);
  if (!Number.isNaN(coercedNumber)) {
    return Math.ceil(num);
  } else {
    return num;
  }
};

const defaultOptions: Pick<Options, 'format' | 'quality'> = {
  format: 'auto',
  quality: 'auto:good',
};

export const toImageCdnSrcWithNoTransforms = (
  /**
   * This function exists because some assets do not require transforms e.g PDFs
   */
  { src }: Src,
) => `${toImageCdnSrcPrefix()}/${toAbsoluteUrl(src)}`;

export const toImageCdnSrc = (
  { src, ...options }: Src & Options,
  blur: number | null = null,
) => {
  const absoluteSrc = toAbsoluteUrl(src);
  const prefix = toImageCdnSrcPrefix();

  // AVIF images don't support transformations - return without any params
  // Extract the pathname, falling back to string splitting for invalid URLs
  let pathname;
  try {
    pathname = new URL(absoluteSrc).pathname;
  } catch (e) {
    // Fallback to string splitting for invalid URLs (e.g., URLs with spaces)
    pathname = absoluteSrc.split('?')[0].split('#')[0];
  }
  const isAvif = /\.avif$/i.test(pathname);
  if (isAvif) {
    return `${prefix}/${absoluteSrc}`;
  }

  const transformString = toTransformParams(options);
  let url = `${prefix}/${transformString}/${absoluteSrc}`;

  if (blur !== null) {
    url = `${prefix}/e_blur:${blur}/${transformString}/${absoluteSrc}`;
  }

  return url;
};

export const toImageCdnSrcOrLocalSrc = (
  // If we're in a local environment and the src string is a relative path, just return the relative path;
  // OR if we're in any environment and the src string is a data URI, just return that.
  // Otherwise, build a CDN url to apply the requested transforms.
  isLocal: boolean,
  options: Options & Src,
): string =>
  (isLocal && isRelativePath(options.src)) ||
  isDataUri(options.src) ||
  isBlobUri(options.src)
    ? options.src
    : toImageCdnSrc(options);

const toTransformParams = (options: Options): string => {
  const _options = {
    ...defaultOptions,
    devicePixelRatio:
      (typeof window !== 'undefined' ? window.devicePixelRatio : undefined) ?? 1,
    ...options,
  };

  // A little trick:
  // `formatTransform` returns an empty array if the value is `undefined`.
  // `concat` doesn't add empty arrays to the final array.
  // Therefore, this gives us an array containing only the set transforms.
  const transforms = ([] as string[]).concat(
    // *** NOTE - ORDER MATTERS! ***
    // The CDN treats these as transforms performed in a specific order.
    // Be sure that any new transforms are put in an order that makes sense.
    // Example: cropping before setting quality may result in a higher-quality image than the reverse
    formatTransform('ar', _options.aspectRatio),
    formatTransform('c', _options.crop),
    formatTransform('co', _options.color),
    formatTransform('dpr', _options.devicePixelRatio.toFixed(1)),
    formatTransform('e', _options.effect),
    formatTransform('f', _options.format),
    formatTransform('g', _options.gravity),
    formatTransform('h', toImageCdnSrcSafeDimension(_options.height)),
    formatTransform('q', _options.quality),
    formatTransform('w', toImageCdnSrcSafeDimension(_options.width)),
    formatTransform('r', _options.radius),
    formatTransform('x', _options.x),
    formatTransform('y', _options.y),
  );

  return transforms.join(',');
};

const formatTransform = (prefix: string, value?: number | string | null) =>
  value != null ? `${prefix}_${value}` : [];
